import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, orderedAccounts, replyModeOf, emailsKey } from '../../store';
import { api } from '../../utils/api';
import { accentVars, ACCOUNT_COLORS } from '../../utils/accountColors';
import { EMAIL_CAL_GROUP } from '../../tabPalette';
import { labelView } from '../../utils/emailLabels';
import { fmtDate, fmtDateTime, localeDate, timeHM, todayISO, tz } from '../../utils/region';
import { useInstallOffer, takeInstallHow } from '../../utils/installApp';
import { useAppUpdate, AppUpdateBar } from '../../utils/appUpdate';
import { Attachment, Email, Folder, ReplyMode } from '../../types';
import { HtmlFrame, fileIcon } from '../EmailDetail';
import { PdfPreview } from '../PdfPreview';
import ComposeNew from '../ComposeNew';
import Composer from '../Composer';
import ScheduledList from '../ScheduledList';

// ── Email: the mail as an app a phone installs (report 0e8dd0cb) ─────────────────────────────────────────────────────
// The report: "Create an email app the same way that we have done for chat and files, downloadable to the phone. Copy
// the layout and styling from Gmail."
// So this is the mail the office already has, at an address of its own (/email), dressed as an app the way Chat and
// Files are, in Gmail's phone layout: a rounded search bar with a menu button and the mailbox's avatar · a slide-out
// drawer with the mailboxes and the folders · rows with a coloured initial, sender, subject, snippet, time and star, bold
// while unread · a floating Compose pill · a full-screen reader with Reply, Reply all and Forward pills along its foot.
// One codebase and no table of its own: everything it does goes through the store the desktop page uses (loadEmails,
// setActiveEmail, markRead, deleteEmail…), the desktop's own HtmlFrame draws a message's body (so the "opened" picture
// stays out, and reading an email here never counts as its recipient opening it), and writing is the desktop's ComposeNew
// and Composer themselves, laid out full-screen below (COMPOSE_CSS), so approval holds, Undo send and Auto CC are
// the same path as on a desk. Job linking, Rectify (AI), labelling and the bulk actions stay on the desktop page.
// Opening #2 added two things to the list: a swipe on a row deletes it or turns its read state round (SwipeRow), and
// the heading row chooses between the whole folder and its unread mail, a choice that is kept (see `fellBack`).
// What makes a phone offer to install it, as for Chat and Files:
// · email.webmanifest (public/) — the name Email, its picture and the address /email in a window of its own.
//   index.html WRITES it into the head on a direct load of /email, before an iPhone looks; useEmailHead below sets it
//   for a walk in from another page (the sign-in page, above all) and puts the site's own back on the way out.
// · sw.js (public/) — the caching-free service worker Chat registers over /chat, registered again over /email.
// · the Install app button under the search bar (utils/installApp.ts): the browser's own dialog where it gave the page
//   one, else the few words that say which of the phone's own buttons to press.
const EMAIL_NAME = 'Email';                       // what the icon is called — no firm's name, as every tenant installs it
const EMAIL_MANIFEST = '/email.webmanifest';
// The picture under that name: the chat's tile with the ring in the top menu's Email purple (report 89771ce1), drawn by
// the server (server/src/routes/chatIcon.ts). email.webmanifest names 192 and 512; this is the one an iPhone reads.
// index.html writes the same address on a direct load; a new ?v= belongs in both.
const EMAIL_ICON = '/api/email-icon/180.png?v=2';
const EMAIL_SCOPE = '/email';
// The screen's very edges, so env(safe-area-inset-*) keeps clear of an iPhone's notch and home bar — and the pinch zoom
// stays, as in Files: mail is read here. index.html writes the very same line on a direct load.
const EMAIL_VIEWPORT = 'width=device-width, initial-scale=1, viewport-fit=cover';

// Gmail's own colours, where the page's CSS variables have nothing of the kind. The page, the words and the lines keep
// the variables; the composers keep the open mailbox's colour, through --accent, as on the desktop. The Install button
// wears the top menu's Email purple (EMAIL_CAL_GROUP, report 89771ce1), whichever mailbox is open.
const GMAIL = {
  red: '#EA4335',             // the word Email at the head of the drawer, "Draft" on a row, behind a row swiped to delete
  blue: '#1A73E8',            // behind a row swiped to mark it read or unread
  bar: '#EAF1FB',             // the search bar
  picked: '#D3E3FD',          // the open mailbox's and the open folder's rows in the drawer, the heading row's choice
  pickedText: '#041E49',
  compose: '#C2E7FF',         // the Compose pill
  composeText: '#001D35',
  star: '#F4B400',
};
// A coloured circle per correspondent, the same one for the same address every time.
const FACE_COLORS = ['#1A73E8', '#D93025', '#188038', '#E37400', '#A142F4', '#12858A', '#C5221F', '#B06000', '#C2185B', '#5F6368'];
const COLUMN = 820;           // on a tablet the list and the reader stop here, centred

// The folders in Gmail's order. The same ones as the desktop's sidebar, and only the same four count: Inbox, Sent, Drafts
// and Spam (Sidebar: NAV). The drawer has the room to show them all, so there is no More / Less here.
const FOLDERS: { key: Folder; icon: string; labelKey: string; counts?: boolean }[] = [
  { key: 'inbox', icon: 'ti-inbox', labelKey: 'inbox', counts: true },
  { key: 'starred', icon: 'ti-star', labelKey: 'starred' },
  { key: 'snoozed', icon: 'ti-clock-pause', labelKey: 'eml_snoozed' },
  { key: 'sent', icon: 'ti-send', labelKey: 'sent', counts: true },
  { key: 'scheduled', icon: 'ti-calendar-time', labelKey: 'eml_scheduled' },
  { key: 'drafts', icon: 'ti-file-text', labelKey: 'drafts', counts: true },
  { key: 'archive', icon: 'ti-archive', labelKey: 'archive' },
  { key: 'spam', icon: 'ti-alert-octagon', labelKey: 'spam', counts: true },
  { key: 'trash', icon: 'ti-trash', labelKey: 'trash' },
];
// Mailboxes that are Spam by another name are the Spam folder's, not labels of their own (as in Sidebar).
const SPAM_BOX_NAME = /^(spam|junk|junk e-?mail|junk mail|bulk mail)$/i;

// ── The composers, full-screen ───────────────────────────────────────────────────────────────────────────────────────
// ComposeNew and Composer draw themselves as a box over a dimmed page, in inline styles, which is right on a desk. While
// this app is the page these rules (in its <style>, so they go with it) turn that same box into the whole screen, reaching
// the composer Undo send opens again as well, which stands outside this page. The Send buttons come up under the
// composer's title, where Gmail keeps its Send: at the foot they would stand behind the phone's keyboard. 16px type in every
// box, because an iPhone zooms the page in on any box typed into at less. The size is what the keyboard leaves
// (useKeyboardRoom). What is typed goes out in the editor's own type (utils/compose) whatever size it is shown at here.
const COMPOSE_CSS = `
.mf-compose { top: var(--emailapp-top, 0px) !important; bottom: auto !important; height: var(--emailapp-height, 100%) !important; box-sizing: border-box; background: var(--bg-surface) !important; padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left) !important; }
.mf-compose > div { max-width: none !important; max-height: none !important; height: 100%; border: none !important; border-radius: 0 !important; box-shadow: none !important; }
.mf-compose > div > div:first-child { order: -2; }
.mf-compose-bar { order: -1; border-top: none !important; border-bottom: 1px solid var(--border); }
.mf-compose-tools, .mf-compose-tools > div { flex-wrap: wrap; }
.mf-compose input, .mf-compose select, .mf-compose .ProseMirror { font-size: 16px !important; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

// A message's body on a phone: pictures and tables kept inside the screen's width, and long words broken, in place of a
// page that has to be dragged sideways. A one-pixel spacer stays one pixel high. Whatever is still wider scrolls sideways in
// its own frame (the reader's overflowX), and the pinch zoom is there for the rest.
const BODY_CSS = '<style>body{margin:12px 16px;overflow-wrap:anywhere;-webkit-text-size-adjust:100%}img,video{max-width:100%!important;height:auto!important}img[height="1"]{height:1px!important}table{max-width:100%!important}pre{white-space:pre-wrap}</style>';
const phoneBody = (html: string): string => (html.includes('<head>') ? html.replace('<head>', `<head>${BODY_CSS}`) : BODY_CSS + html);

const initialOf = (name: string): string => {
  const first = Array.from((name || '').trim().replace(/^[\s"'(<[{_*.,;:!?#~-]+/, ''))[0];
  return first ? String(first).toUpperCase() : '?';
};
const colorOf = (key: string): string => {
  let h = 0;
  for (let i = 0; i < (key || '').length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return FACE_COLORS[Math.abs(h) % FACE_COLORS.length];
};
const fmtBytes = (n: number): string => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`);
const escHtml = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const oneLine: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

/** A row's time, in the tenant's zone and day first: 14:05 today, 12 Oct this year, 12/10/2025 before that. */
function rowTime(d: Date): string {
  if (!d || isNaN(d.getTime())) return '';
  const day = todayISO(d), today = todayISO();
  if (day === today) return timeHM(d);
  if (day.slice(0, 4) === today.slice(0, 4)) {
    try { return new Intl.DateTimeFormat(localeDate(), { day: 'numeric', month: 'short', timeZone: tz() }).format(d); } catch { /* the plain date below */ }
  }
  return fmtDate(day);
}

/** One message of GET /emails/thread as the composers take it (ThreadView does the same on the desktop). */
function toEmail(m: any): Email {
  return {
    id: m.id, accountId: m.account_id, subject: m.subject,
    from: { name: m.from_name, email: m.from_email },
    to: JSON.parse(m.to_list || '[]'), cc: JSON.parse(m.cc_list || '[]'), bcc: [],
    date: new Date(m.date * 1000), lang: m.detected_lang,
    bodyOriginal: m.body_html || (m.body_text ? `<pre style="white-space:pre-wrap;font-family:inherit;margin:0">${escHtml(String(m.body_text))}</pre>` : ''),
    bodyTranslated: JSON.parse(m.translations || '{}'),
    attachments: JSON.parse(m.attachments || '[]'), unread: false, starred: Boolean(m.starred),
    folder: m.folder, labels: m.labels ? JSON.parse(m.labels) : [], preview: m.preview || '',
  };
}

/** An attachment's bytes, fetched with the login token, which a plain link cannot carry. null if the server refuses. */
async function fetchAttachment(emailId: string, attId: string): Promise<Blob | null> {
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const res = await fetch(`${apiBase}/emails/${emailId}/attachments/${attId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('mf_token')}` } });
  return res.ok ? res.blob() : null;
}

/** The page's head while Email is the page: its own manifest, its own picture, its own name for an iPhone icon and its
 *  own viewport, all four put back as they were on the way out, so every other page installs as the portal and zooms
 *  as it always has. On a direct load of /email index.html has written them already; this is the walk in from another page. */
function useEmailHead(): void {
  useEffect(() => {
    // data-site-href / data-site-content carry the SITE's own, which index.html writes on every page — so the way out
    // restores the site's even after a direct load of /email.
    const link = document.head.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const was = link?.dataset.siteHref || link?.getAttribute('href') || '';
    link?.setAttribute('href', EMAIL_MANIFEST);
    const touch = document.head.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    const wasTouch = touch?.dataset.siteHref || touch?.getAttribute('href') || '';
    touch?.setAttribute('href', EMAIL_ICON);
    const already = document.head.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    const named = already || document.createElement('meta');
    named.name = 'apple-mobile-web-app-title';
    named.content = EMAIL_NAME;
    if (!already) document.head.appendChild(named);
    const view = document.head.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    const wasView = view?.dataset.siteContent || view?.getAttribute('content') || '';
    view?.setAttribute('content', EMAIL_VIEWPORT);
    // Stores nothing (see sw.js), so an installed Email is never served a page older than the last deploy. A browser
    // with no service workers simply has none, and the page works the same — only the Install offer is missing.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js', { scope: EMAIL_SCOPE }).catch(() => { /* not installable, still usable */ });
    }
    return () => {
      if (link && was) link.setAttribute('href', was);
      if (touch && wasTouch) touch.setAttribute('href', wasTouch);
      if (view && wasView) view.setAttribute('content', wasView);
      named.remove();
    };
  }, []);
}

/** The phone's own Back closes what stands over the list, as in Gmail, instead of leaving the app: Android's back button
 *  has nothing else to go back to in an installed window. One history step per layer open (`depth`). A press of Back takes
 *  one off and closes the top layer (`closeTop`); a layer closed any other way takes its own step off again, and the
 *  popstate that brings is no press of Back (`leaving`). The steps carry the router's own state, so the address and
 *  the router notice nothing. */
function useBackLayers(depth: number, closeTop: () => void): void {
  const pushed = useRef(0);
  const leaving = useRef(0);
  const close = useRef(closeTop);
  close.current = closeTop;
  useEffect(() => {
    if (depth > pushed.current) {
      for (; pushed.current < depth; pushed.current++) window.history.pushState(window.history.state, '');
    } else if (depth < pushed.current) {
      const n = pushed.current - depth;
      pushed.current = depth;
      leaving.current += 1;   // one go() brings one popstate, however many steps it takes
      window.history.go(-n);
    }
  }, [depth]);
  useEffect(() => {
    const onPop = () => {
      if (leaving.current > 0) { leaving.current -= 1; return; }
      if (pushed.current > 0) { pushed.current -= 1; close.current(); }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
}

/** How much of the screen the phone's keyboard leaves, for the composers to fill (COMPOSE_CSS), so what is being typed is
 *  never behind it. Pinched in, the part in view is no measure of that, and the composer keeps the whole screen. */
function useKeyboardRoom(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    const fit = () => {
      const zoomed = vv.scale > 1.01;
      root.style.setProperty('--emailapp-top', `${zoomed ? 0 : vv.offsetTop}px`);
      root.style.setProperty('--emailapp-height', zoomed ? '100%' : `${vv.height}px`);
    };
    fit();
    vv.addEventListener('resize', fit);
    vv.addEventListener('scroll', fit);
    return () => {
      vv.removeEventListener('resize', fit);
      vv.removeEventListener('scroll', fit);
      root.style.removeProperty('--emailapp-top');
      root.style.removeProperty('--emailapp-height');
    };
  }, []);
}

/** A correspondent's coloured circle with their initial. */
function Face({ name, colorKey, size }: { name: string; colorKey: string; size: number }) {
  return (
    <div aria-hidden style={{ width: size, height: size, borderRadius: '50%', background: colorOf(colorKey), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * 0.45), fontWeight: 500, flexShrink: 0 }}>
      <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{initialOf(name)}</span>
    </div>
  );
}

// One list row. In Sent and Drafts the sender is always this mailbox, so the row names who it went to instead, as Gmail's
// does. A conversation names its people and its size, as on the desktop list.
const Row = React.memo(function Row({ email, folder, onOpen, onStar }: { email: Email; folder: Folder; onOpen: (e: Email) => void; onStar: (e: Email) => void }) {
  const { t } = useTranslation();
  const drafts = folder === 'drafts';
  const sent = folder === 'sent';
  const unread = email.unread && !drafts;
  const conv = (email.threadCount || 1) > 1;
  const toLine = email.to.join(', ');
  const fromName = email.from.name || email.from.email;
  const who = drafts ? (toLine || t('emailapp_no_recipients'))
    : sent ? `${t('to')}: ${toLine}`
      : conv && email.participants?.length ? email.participants.map(p => (p.name || p.email).split(' ')[0]).join(', ')
        : fromName;
  const face = (drafts || sent) ? (email.to[0] || fromName) : fromName;
  const faceKey = (drafts || sent) ? (email.to[0] || email.from.email) : email.from.email;
  return (
    <div role="button" tabIndex={0} onClick={() => onOpen(email)} onKeyDown={e => { if (e.key === 'Enter' && e.target === e.currentTarget) onOpen(email); }}
      style={{ display: 'flex', gap: 12, padding: '10px 14px 10px 16px', cursor: 'pointer' }}>
      <Face name={face} colorKey={faceKey || face} size={40} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ ...oneLine, flex: 1, minWidth: 0, fontSize: 16, lineHeight: '22px', fontWeight: unread ? 700 : 400, color: 'var(--text-primary)' }}>
            {drafts && <span style={{ color: GMAIL.red, marginRight: 6 }}>{t('emailapp_draft')}</span>}
            {who}
            {conv && <span style={{ marginLeft: 6, fontSize: 13, fontWeight: 400, color: 'var(--text-secondary)' }}>{email.threadCount}</span>}
          </span>
          <span style={{ fontSize: 12, flexShrink: 0, fontWeight: unread ? 700 : 400, color: unread ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{rowTime(email.date)}</span>
        </div>
        <div style={{ ...oneLine, fontSize: 14, lineHeight: '20px', fontWeight: unread ? 700 : 400, color: unread ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{email.subject || t('emailapp_no_subject')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 20 }}>
          <span style={{ ...oneLine, flex: 1, minWidth: 0, fontSize: 14, lineHeight: '20px', color: 'var(--text-secondary)' }}>{email.preview || ''}</span>
          {email.attachments.length > 0 && <i className="ti ti-paperclip" style={{ fontSize: 15, color: 'var(--text-secondary)', flexShrink: 0 }} aria-hidden />}
          {!drafts && (
            <button onClick={e => { e.stopPropagation(); onStar(email); }} title={t(email.starred ? 'emailapp_unstar' : 'emailapp_star')} aria-label={t(email.starred ? 'emailapp_unstar' : 'emailapp_star')} aria-pressed={email.starred}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, margin: '-6px -6px -6px 0', padding: 0, border: 'none', borderRadius: '50%', background: 'transparent', color: email.starred ? GMAIL.star : 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
              <i className={`ti ti-star${email.starred ? '-filled' : ''}`} style={{ fontSize: 20 }} aria-hidden />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

// ── A swipe on a list row ────────────────────────────────────────────────────────────────────────────────────────────
// Opening #2 of the report: "Allow users to swipe right for each email in their inbox to delete and swipe left to mark
// as read." Opening #3: "Do the vice versa: if the email is already read allow a left swipe to mark as unread." The row
// slides with the finger over a coloured action; let go short of SWIPE_FIRE and it springs back with nothing done.
const SWIPE_SLOP = 10;        // px: the finger's first move, which settles what the gesture is
const SWIPE_FIRE = 100;       // px: how far the row has to travel for its action to happen
const SWIPE_MS = 180;         // the row's slide back into place, or off the screen
const SWIPE_TAP_MS = 400;     // a click this soon after a swipe belongs to the swipe: it is no press of the row
const SWIPE_BACK = {
  delete: { icon: 'ti-trash', background: GMAIL.red, color: '#fff', justifyContent: 'flex-start' },
  read: { icon: 'ti-mail-opened', background: GMAIL.blue, color: '#fff', justifyContent: 'flex-end' },
  unread: { icon: 'ti-mail', background: GMAIL.blue, color: '#fff', justifyContent: 'flex-end' },
};

/** The touch handling around a list row. The first move settles the gesture for good: clearly sideways is a swipe, and
 *  anything else is the list scrolling, which the browser does by itself (pan-y) with this staying out of it. Two fingers
 *  are a pinch and, pinched in, a sideways drag is a look around: neither is a swipe, as a swipe right takes the email
 *  away with no question asked. pinch-zoom stays allowed, as in the viewport (EMAIL_VIEWPORT). A swipe left turns the
 *  read state round: an unread row goes read, a read row goes unread. Drafts have no swipe left: the row stays put. */
function SwipeRow({ email, folder, onDelete, onMarkRead, onMarkUnread, children }: {
  email: Email; folder: Folder; onDelete: (e: Email) => void; onMarkRead: (e: Email) => void;
  onMarkUnread: (e: Email) => void; children: React.ReactNode;
}) {
  const [dx, setDx] = useState(0);
  const [held, setHeld] = useState(false);     // under the finger: the row follows it with no animation
  const drag = useRef<{ x: number; y: number; sideways: boolean | null; dx: number } | null>(null);
  const side = useRef<keyof typeof SWIPE_BACK | null>(null);   // what is behind the row, kept while it springs back
  const endedAt = useRef(0);                   // when the last swipe ended
  const leftAction: 'read' | 'unread' | null = folder === 'drafts' ? null : email.unread ? 'read' : 'unread';
  if (held && dx) side.current = dx > 0 ? 'delete' : leftAction;
  const back = side.current && SWIPE_BACK[side.current];
  const armed = Math.abs(dx) >= SWIPE_FIRE;

  // Let go (fire), or taken away by the browser or a second finger (no fire).
  const release = (fire: boolean) => {
    const d = drag.current;
    drag.current = null;
    if (!d?.sideways) return;
    endedAt.current = Date.now();
    setHeld(false);
    const far = fire ? d.dx : 0;
    if (far >= SWIPE_FIRE && email.folder !== 'trash') {
      // Off the screen, then out of the list. In Trash it comes back instead, with the question over it (onDelete).
      setDx(window.innerWidth);
      window.setTimeout(() => { onDelete(email); setDx(0); }, SWIPE_MS);
      return;
    }
    setDx(0);
    if (far >= SWIPE_FIRE) onDelete(email);
    else if (far <= -SWIPE_FIRE && leftAction === 'read') onMarkRead(email);
    else if (far <= -SWIPE_FIRE && leftAction === 'unread') onMarkUnread(email);
  };

  return (
    <div
      onTouchStart={e => {
        if (e.touches.length > 1 || (window.visualViewport?.scale || 1) > 1.01) { release(false); return; }
        drag.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, sideways: null, dx: 0 };
      }}
      onTouchMove={e => {
        const d = drag.current;
        if (!d) return;
        if (e.touches.length > 1) { release(false); return; }
        const mx = e.touches[0].clientX - d.x, my = e.touches[0].clientY - d.y;
        if (d.sideways === null) {
          if (Math.abs(mx) < SWIPE_SLOP && Math.abs(my) < SWIPE_SLOP) return;
          // Only a clearly sideways move: a slanted flick up the list stays a scroll and slides no row.
          d.sideways = Math.abs(mx) > Math.abs(my) * 1.5;
          if (d.sideways) setHeld(true);
        }
        if (!d.sideways) return;
        d.dx = mx < 0 && !leftAction ? 0 : mx;
        setDx(d.dx);
      }}
      onTouchEnd={() => release(true)}
      onTouchCancel={() => release(false)}
      onClickCapture={e => { if (Date.now() - endedAt.current < SWIPE_TAP_MS) { e.preventDefault(); e.stopPropagation(); } }}
      style={{ position: 'relative', overflow: back ? 'hidden' : undefined, touchAction: 'pan-y pinch-zoom' }}>
      {back && (
        <div aria-hidden style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', padding: '0 24px', background: back.background, color: back.color, justifyContent: back.justifyContent }}>
          <i className={`ti ${back.icon}`} style={{ fontSize: 24, transform: armed ? 'scale(1.25)' : 'none', transition: 'transform 120ms ease' }} />
        </div>
      )}
      <div style={{ position: 'relative', background: 'var(--bg-surface)', transform: dx ? `translateX(${dx}px)` : 'none', transition: held ? 'none' : `transform ${SWIPE_MS}ms ease` }}>
        {children}
      </div>
    </div>
  );
}

/** The slide-out drawer: the mailboxes with their unread counts, then the open mailbox's folders and labels with theirs. */
function Drawer({ open, onClose, onMailbox, onFolder }: { open: boolean; onClose: () => void; onMailbox: (id: string) => void; onFolder: (f: Folder) => void }) {
  const { t } = useTranslation();
  const { accounts, accountOrder, defaultAccountId, activeAccountId, activeFolder, folders, labelOverrides, labelUnread, folderUnread } = useStore();
  const acct = activeAccountId || '';
  const counts = folderUnread[acct || 'all'];
  const labelCounts = labelUnread[acct];
  const overrides = labelOverrides[acct];
  const labels = (folders[acct] || [])
    .filter(f => f.kind === 'label' && f.path !== '[Gmail]' && !SPAM_BOX_NAME.test(f.name) && !labelView(f, overrides).hidden)
    .sort((a, b) => a.path.localeCompare(b.path));
  const inbox = accounts.filter(a => !activeAccountId || a.id === activeAccountId).reduce((s, a) => s + a.unread, 0);

  const row = (key: string, lead: React.ReactNode, label: string, sub: string, count: number, picked: boolean, onClick: (() => void) | undefined, indent = 0) => (
    <button key={key} onClick={onClick} disabled={!onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 14, width: 'calc(100% - 8px)', minHeight: sub ? 56 : 48, padding: `4px 18px 4px ${16 + indent}px`, border: 'none', borderRadius: '0 24px 24px 0', background: picked ? GMAIL.picked : 'transparent', color: picked ? GMAIL.pickedText : 'var(--text-primary)', fontWeight: picked || count > 0 ? 700 : 400, textAlign: 'left', cursor: onClick ? 'pointer' : 'default', opacity: onClick ? 1 : 0.55, flexShrink: 0 }}>
      {lead}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...oneLine, display: 'block', fontSize: 14 }}>{label}</span>
        {!!sub && <span style={{ ...oneLine, display: 'block', fontSize: 12, fontWeight: 400, color: picked ? GMAIL.pickedText : 'var(--text-secondary)' }}>{sub}</span>}
      </span>
      {count > 0 && <span style={{ fontSize: 12, flexShrink: 0 }}>{count}</span>}
    </button>
  );
  const heading = (text: string) => <div style={{ padding: '14px 16px 4px', fontSize: 12, color: 'var(--text-secondary)' }}>{text}</div>;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'rgba(0,0,0,0.32)', opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none', transition: 'opacity 200ms ease' }} />
      <nav aria-hidden={!open} style={{ position: 'fixed', top: 0, bottom: 0, left: 0, width: 'min(304px, 84vw)', zIndex: 1401, display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: '0 16px 16px 0', boxShadow: open ? 'var(--shadow-md)' : 'none', transform: open ? 'none' : 'translateX(-105%)', visibility: open ? 'visible' : 'hidden', transition: 'transform 200ms ease, visibility 200ms', paddingTop: 'env(safe-area-inset-top)', paddingLeft: 'env(safe-area-inset-left)', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 16px 8px', flexShrink: 0 }}>
          <i className="ti ti-mail" style={{ fontSize: 24, color: GMAIL.red }} aria-hidden />
          <span style={{ fontSize: 20, fontWeight: 500, color: GMAIL.red }}>{t('tab_email')}</span>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
          {heading(t('accounts'))}
          {orderedAccounts({ accounts, accountOrder, defaultAccountId }).map(a => {
            const c = ACCOUNT_COLORS[a.color] || ACCOUNT_COLORS.teal;
            return row(a.id,
              <span aria-hidden style={{ width: 32, height: 32, borderRadius: '50%', background: c.accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 500, flexShrink: 0 }}>{initialOf(a.name || a.email)}</span>,
              a.name || a.email, a.email, a.unread, a.id === activeAccountId, () => onMailbox(a.id));
          })}
          <div style={{ height: 1, background: 'var(--border)', margin: '10px 16px 4px', flexShrink: 0 }} />
          {FOLDERS.map(f => row(f.key,
            <i className={`ti ${f.icon}`} style={{ fontSize: 20, flexShrink: 0 }} aria-hidden />,
            t(f.labelKey), '', !f.counts ? 0 : f.key === 'inbox' ? inbox : (counts?.[f.key] || 0), activeFolder === f.key, () => onFolder(f.key)))}
          {labels.length > 0 && heading(t('labels'))}
          {labels.map(f => {
            const v = labelView(f, overrides);
            const depth = Math.max(0, f.path.split(f.delimiter || '/').length - 1);
            return row(f.path,
              <i className="ti ti-tag" style={{ fontSize: 20, flexShrink: 0, color: v.color || undefined }} aria-hidden />,
              v.name, '', f.selectable ? (labelCounts?.[f.path] || 0) : 0, activeFolder === f.path, f.selectable ? () => onFolder(f.path) : undefined, depth * 12);
          })}
        </div>
      </nav>
    </>
  );
}

type Asking = { text: string; yes: string; danger: boolean; run: () => void };

/** A yes-or-no before an email leaves the list, as on the desktop: a phone is tapped by accident more than a desk is, and
 *  a Delete in Trash cannot be undone. Asked by the Reader's buttons, and by the list for a swipe in Trash. */
function Ask({ text, yes, danger, onYes, onNo }: { text: string; yes: string; danger: boolean; onYes: () => void; onNo: () => void }) {
  const { t } = useTranslation();
  const btn: React.CSSProperties = { padding: '9px 14px', border: 'none', borderRadius: 18, background: 'transparent', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
  return (
    <div role="dialog" aria-modal="true" onMouseDown={e => { if (e.target === e.currentTarget) onNo(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1900, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 340, background: 'var(--bg-surface)', borderRadius: 24, padding: '22px 18px 12px 22px', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--text-primary)', marginBottom: 14 }}>{text}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <button onClick={onNo} style={{ ...btn, color: 'var(--text-secondary)' }}>{t('cancel')}</button>
          <button onClick={onYes} style={{ ...btn, color: danger ? GMAIL.red : 'var(--accent)' }}>{yes}</button>
        </div>
      </div>
    </div>
  );
}

/** The open email, full-screen: back, archive, delete and mark unread across the top, Reply, Reply all and Forward along the
 *  foot. A conversation is its messages oldest first, the newest open and the rest folded to a line, one press from opening,
 *  as in Gmail. Opening a conversation reads it: all its unread messages go read at once. The pills answer the message
 *  opened last. `busy`: a single email's body is on its way (store.setActiveEmail); a conversation fetches its own here. */
function Reader({ root, busy, onBack, onRetry, onWrite, onAttachment }: {
  root: Email; busy: boolean; onBack: () => void; onRetry: (id: string) => void;
  onWrite: (email: Email, mode: ReplyMode) => void; onAttachment: (emailId: string, att: Attachment) => void;
}) {
  const { t } = useTranslation();
  const { archiveEmail, deleteEmail, deleteEmails, markRead, markUnread, setActiveEmail, setStar, showToast, toggleStar, replyAllDefault } = useStore();
  const conv = (root.threadCount || 1) > 1;
  const [msgs, setMsgs] = useState<Email[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(conv);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState('');
  const [ask, setAsk] = useState<Asking | null>(null);
  const known = useRef<Set<string>>(new Set());      // the messages seen so far: only a new one opens on a refetch
  const marked = useRef<Set<string>>(new Set());     // the messages marked read, so a refetch cannot mark one twice
  const cards = useRef<Record<string, HTMLDivElement | null>>({});

  // The list's refresh is what brings a new reply here, so the conversation is fetched again when its row changes.
  useEffect(() => {
    if (!conv) return;
    let alive = true;
    const first = known.current.size === 0;
    if (first) { setLoading(true); setFailed(false); }
    api.getThread(root.threadId || root.id, root.accountId).then((d: any) => {
      if (!alive) return;
      const rows: any[] = d.emails || [];
      const list = rows.map(m => toEmail(m));
      const last = list[list.length - 1];
      const lastIsNew = !!last && !known.current.has(last.id);
      setMsgs(list); setTotal(d.total || list.length); setLoading(false); setFailed(false);
      // The row's own message first: that clears the row's unread count whole, whatever the count said (store.markRead).
      const unread: string[] = rows.filter(m => m.unread && !marked.current.has(m.id)).map(m => m.id);
      unread.forEach(id => marked.current.add(id));
      unread.filter(id => id === root.id).concat(unread.filter(id => id !== root.id)).forEach(id => { void markRead(id); });
      if (first && last) {
        setOpenIds(new Set([last.id]));
        setTarget(last.id);
        // Land on the top of the newest message, as the desktop does: a long conversation folds to many lines above it.
        window.setTimeout(() => cards.current[last.id]?.scrollIntoView({ block: 'start' }), 50);
      } else if (lastIsNew) {
        setOpenIds(prev => new Set(prev).add(last.id));
        setTarget(last.id);
      }
      list.forEach(m => known.current.add(m.id));
    }).catch(() => { if (alive && first) { setLoading(false); setFailed(true); } });
    return () => { alive = false; };
  }, [conv, root.threadId, root.id, root.accountId, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = conv ? msgs : [root];
  const last = list[list.length - 1];
  const replyTo = list.find(m => m.id === target) || last;
  // A reply quotes the message and a forward carries it, so the pills wait for its body.
  const canAnswer = conv ? !loading && !failed && !!replyTo : !!root.bodyOriginal;
  const convIds = msgs.length ? msgs.map(m => m.id) : (root.threadIds || [root.id]);
  const forever = root.folder === 'trash';

  const archive = () => setAsk(conv
    ? { text: t('emailapp_ask_archive_conv', { n: convIds.length }), yes: t('archive'), danger: false, run: () => { convIds.forEach(id => { void archiveEmail(id); }); setActiveEmail(null); showToast(t('emailapp_conv_archived')); } }
    : { text: t('emailapp_ask_archive'), yes: t('archive'), danger: false, run: () => { void archiveEmail(root.id); setActiveEmail(null); showToast(t('emailapp_archived')); } });
  const remove = () => setAsk(conv
    ? { text: t('emailapp_ask_trash_conv', { n: convIds.length }), yes: t('delete'), danger: true, run: () => { void deleteEmails(convIds); setActiveEmail(null); showToast(t('emailapp_conv_trashed')); } }
    : { text: t(forever ? 'emailapp_ask_delete' : 'emailapp_ask_trash'), yes: t('delete'), danger: true, run: () => { void deleteEmail(root.id); setActiveEmail(null); showToast(t(forever ? 'emailapp_deleted' : 'emailapp_trashed')); } });
  // In a conversation the newest message goes back to unread; either way the email closes, so the list shows it unread.
  const unread = () => { void markUnread(conv ? (last?.id || root.id) : root.id); showToast(t('emailapp_marked_unread')); setActiveEmail(null); };
  const flipStar = () => {
    if (conv && root.threadIds?.length) root.threadIds.forEach(id => { void setStar(id, !root.starred); });
    else void toggleStar(root.id);
  };
  const toggle = (id: string) => {
    const opening = !openIds.has(id);
    setOpenIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
    if (opening) setTarget(id);
    else if (target === id) setTarget(last?.id || '');
  };

  const barBtn = (icon: string, label: string, onClick: () => void, off = false) => (
    <button onClick={onClick} disabled={off} title={label} aria-label={label}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, padding: 0, border: 'none', borderRadius: '50%', background: 'transparent', color: 'var(--text-secondary)', cursor: off ? 'default' : 'pointer', opacity: off ? 0.4 : 1, flexShrink: 0 }}>
      <i className={`ti ${icon}`} style={{ fontSize: 22 }} aria-hidden />
    </button>
  );
  const note = (text: string, retry?: () => void) => (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)', fontSize: 14 }}>
      <div>{text}</div>
      {retry && <button onClick={retry} style={{ marginTop: 14, padding: '9px 18px', borderRadius: 20, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{t('emailapp_try_again')}</button>}
    </div>
  );
  const spinner = <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 24, display: 'inline-block', animation: 'spin 1s linear infinite' }} aria-hidden /></div>;

  return (
    <div role="dialog" aria-modal="true" aria-label={root.subject}
      style={{ position: 'fixed', inset: 0, zIndex: 1500, display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', padding: 'env(safe-area-inset-top) env(safe-area-inset-right) 0 env(safe-area-inset-left)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '4px 6px', flexShrink: 0 }}>
        {barBtn('ti-arrow-left', t('files_back'), onBack)}
        <div style={{ flex: 1 }} />
        {root.folder !== 'archive' && barBtn('ti-archive', t(conv ? 'eml_bar_archive_conv' : 'archive'), archive)}
        {barBtn('ti-trash', t(conv ? 'eml_bar_delete_conv' : 'delete'), remove)}
        {/* Off while the body is on its way: that fetch marks it read, and would undo this. */}
        {barBtn('ti-mail', t('eml_mark_unread'), unread, busy)}
      </header>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
        <div style={{ width: '100%', maxWidth: COLUMN, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 10px 10px 16px' }}>
            <h1 style={{ flex: 1, minWidth: 0, margin: 0, fontSize: 20, lineHeight: '28px', fontWeight: 400, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
              {root.subject || t('emailapp_no_subject')}
              {conv && <span style={{ marginLeft: 8, fontSize: 14, color: 'var(--text-secondary)' }}>{total || msgs.length || root.threadCount}</span>}
            </h1>
            <button onClick={flipStar} title={t(root.starred ? 'emailapp_unstar' : 'emailapp_star')} aria-label={t(root.starred ? 'emailapp_unstar' : 'emailapp_star')} aria-pressed={root.starred}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, marginTop: -4, padding: 0, border: 'none', borderRadius: '50%', background: 'transparent', color: root.starred ? GMAIL.star : 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
              <i className={`ti ti-star${root.starred ? '-filled' : ''}`} style={{ fontSize: 22 }} aria-hidden />
            </button>
          </div>

          {conv && loading && spinner}
          {conv && failed && note(t('emailapp_load_fail'), () => setTick(v => v + 1))}
          {conv && !loading && !failed && total > msgs.length && (
            <div style={{ padding: '0 16px 8px', fontSize: 12, color: 'var(--text-secondary)' }}>{t('emailapp_cut', { shown: msgs.length, total })}</div>
          )}
          {(!conv || (!loading && !failed)) && list.map(m => {
            const name = m.from.name || m.from.email || '?';
            const key = m.from.email || name;
            if (conv && !openIds.has(m.id)) {
              return (
                <button key={m.id} onClick={() => toggle(m.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '10px 16px', border: 'none', borderTop: '1px solid var(--border)', background: 'transparent', textAlign: 'left', cursor: 'pointer' }}>
                  <Face name={name} colorKey={key} size={32} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ ...oneLine, display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{name}</span>
                    <span style={{ ...oneLine, display: 'block', fontSize: 13, color: 'var(--text-secondary)' }}>{m.preview || ''}</span>
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0 }}>{rowTime(m.date)}</span>
                </button>
              );
            }
            const recipients = [m.to.length ? `${t('to')}: ${m.to.join(', ')}` : '', m.cc.length ? `${t('cc')}: ${m.cc.join(', ')}` : ''].filter(Boolean).join(' · ');
            return (
              <div key={m.id} ref={el => { cards.current[m.id] = el; }} style={{ borderTop: conv ? '1px solid var(--border)' : 'none' }}>
                <div onClick={conv ? () => toggle(m.id) : undefined} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', cursor: conv ? 'pointer' : 'default' }}>
                  <Face name={name} colorKey={key} size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...oneLine, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{name}</div>
                    <div style={{ ...oneLine, fontSize: 12, color: 'var(--text-secondary)' }}>{m.from.email}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{fmtDateTime(m.date)}</div>
                  </div>
                </div>
                {!!recipients && (
                  <div style={{ padding: '0 16px 8px', fontSize: 12, lineHeight: 1.45, color: 'var(--text-secondary)', overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' } as React.CSSProperties}>{recipients}</div>
                )}
                {m.bodyOriginal ? (
                  <div style={{ overflowX: 'auto', background: '#fff' }}><HtmlFrame html={phoneBody(m.bodyOriginal)} /></div>
                ) : conv ? (
                  <div style={{ padding: '4px 16px 12px', color: 'var(--text-tertiary)' }}>—</div>
                ) : busy ? spinner : note(t('emailapp_load_fail'), () => onRetry(m.id))}
                {m.attachments.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 16px' }}>
                    {m.attachments.map(att => (
                      <button key={att.id} onClick={() => onAttachment(m.id, att)}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '10px 12px', border: '1px solid var(--border-strong)', borderRadius: 12, background: 'transparent', textAlign: 'left', cursor: 'pointer' }}>
                        <i className={`ti ${fileIcon(att.type || '')}`} style={{ fontSize: 24, color: GMAIL.red, flexShrink: 0 }} aria-hidden />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ ...oneLine, display: 'block', fontSize: 14, color: 'var(--text-primary)' }}>{att.name}</span>
                          {Number(att.size) > 0 && <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)' }}>{fmtBytes(Number(att.size))}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ height: 16 }} />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '8px 12px calc(8px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--bg-surface)', flexShrink: 0 }}>
        {/* No Reply while the Reply all switch is on — it would only open as Reply All (report 643e2141) */}
        {([['reply', 'ti-corner-up-left'], ['replyAll', 'ti-corner-up-left-double'], ['forward', 'ti-corner-up-right']] as const)
          .filter(([mode]) => mode !== 'reply' || !replyAllDefault)
          .map(([mode, icon]) => (
            <button key={mode} onClick={() => onWrite(replyTo, replyModeOf(mode))} disabled={!canAnswer}
              style={{ flex: 1, minWidth: 0, maxWidth: 220, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 40, padding: '0 8px', borderRadius: 20, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)', fontSize: 14, fontWeight: 500, cursor: canAnswer ? 'pointer' : 'default', opacity: canAnswer ? 1 : 0.5 }}>
              <i className={`ti ${icon}`} style={{ fontSize: 18, flexShrink: 0 }} aria-hidden />
              <span style={oneLine}>{t(mode)}</span>
            </button>
          ))}
      </div>

      {ask && <Ask text={ask.text} yes={ask.yes} danger={ask.danger} onNo={() => setAsk(null)} onYes={() => { const run = ask.run; setAsk(null); run(); }} />}
    </div>
  );
}

/** What navigator.share / canShare take here — a file and its name. Typed here, as not every TypeScript DOM library
 *  carries the files a share may hold. */
type SharePayload = { files: File[]; title?: string };

/** An attachment, full-screen: Files' sheet (FileLibrary/FilesApp.tsx: FileSheet) for a file out of an email. The desktop's
 *  preview hands a PDF to the browser's own viewer, which a phone does not have in a page, and its one row of buttons runs
 *  off a phone's screen. Fetched once, as the sheet opens, so both buttons answer straight from the press: a phone refuses
 *  a share that waited on the network first. */
function AttachmentSheet({ emailId, att, onClose }: { emailId: string; att: Attachment; onClose: () => void }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [failed, setFailed] = useState(false);
  const [noPicture, setNoPicture] = useState(false);
  const name = att.name || 'attachment';
  const type = (att.type || '').toLowerCase();
  const ext = (/\.([a-z0-9]+)$/i.exec(name)?.[1] || '').toLowerCase();
  const pdf = type.includes('pdf') || ext === 'pdf';
  const picture = !noPicture && (type.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'avif'].includes(ext));
  const nav = navigator as unknown as { share?: (d: SharePayload) => Promise<void>; canShare?: (d: SharePayload) => boolean };

  useEffect(() => {
    let live = true, made = '';
    fetchAttachment(emailId, att.id)
      .then(b => {
        if (!live) return;
        if (!b) { setFailed(true); return; }
        made = URL.createObjectURL(b);
        setBlob(b); setUrl(made);
      })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [emailId, att.id]);

  const download = () => {
    if (!url) return;
    const a = document.createElement('a'); a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const share = async () => {
    if (!blob || !nav.share) return;
    // A file the server would not show inline comes back untyped; its stored type is the better name for it.
    const fileType = blob.type && blob.type !== 'application/octet-stream' ? blob.type : (att.type || blob.type || 'application/octet-stream');
    const data: SharePayload = { files: [new File([blob], name, { type: fileType })], title: name };
    if (nav.canShare && !nav.canShare(data)) { download(); return; }
    try { await nav.share(data); }
    catch (e: any) { if (e?.name !== 'AbortError') download(); }   // AbortError = the sheet was closed on purpose
  };

  const body = failed ? (
    <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-tertiary)', fontSize: 13.5 }}>
      <i className="ti ti-file-alert" style={{ fontSize: 28, display: 'block', marginBottom: 8 }} aria-hidden />{t('files_open_fail')}
    </div>
  ) : !url ? (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 24, display: 'inline-block', animation: 'spin 1s linear infinite' }} aria-hidden /></div>
  ) : pdf ? (
    <PdfPreview src={url} />
  ) : picture ? (
    <img src={url} alt="" onError={() => setNoPicture(true)} style={{ width: '100%', display: 'block', borderRadius: 6, background: '#fff' }} />
  ) : (
    <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-secondary)', fontSize: 13.5 }}>
      <i className={`ti ${fileIcon(att.type || '')}`} style={{ fontSize: 44, display: 'block', marginBottom: 10, color: 'var(--text-tertiary)' }} aria-hidden />
      {Number(att.size) > 0 && <div style={{ marginBottom: 6, fontSize: 12.5, color: 'var(--text-tertiary)' }}>{fmtBytes(Number(att.size))}</div>}
      {t('files_no_preview')}
    </div>
  );
  const ready = !!url && !failed;
  const bigBtn = (primary: boolean, off: boolean): React.CSSProperties => ({
    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '12px 10px', borderRadius: 24,
    fontSize: 14.5, fontWeight: 600, cursor: off ? 'default' : 'pointer', opacity: off ? 0.5 : 1,
    border: primary ? 'none' : '1px solid var(--border-strong)', background: primary ? GMAIL.compose : 'transparent', color: primary ? GMAIL.composeText : 'var(--text-primary)',
  });

  return (
    <div role="dialog" aria-modal="true" aria-label={name}
      style={{ position: 'fixed', inset: 0, zIndex: 1800, display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', padding: 'env(safe-area-inset-top) env(safe-area-inset-right) 0 env(safe-area-inset-left)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px 4px 6px', flexShrink: 0 }}>
        <button onClick={onClose} title={t('files_back')} aria-label={t('files_back')}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, padding: 0, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
          <i className="ti ti-arrow-left" style={{ fontSize: 22 }} aria-hidden />
        </button>
        <div style={{ ...oneLine, flex: 1, minWidth: 0, fontSize: 15, color: 'var(--text-primary)' }}>{name}</div>
      </header>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: 'var(--c-gray-100)', padding: 10 }}>
        <div style={{ width: '100%', maxWidth: 1000, margin: '0 auto' }}>{body}</div>
      </div>
      <div style={{ display: 'flex', gap: 10, padding: '10px 12px calc(10px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--bg-surface)', flexShrink: 0 }}>
        <button onClick={download} disabled={!ready} style={bigBtn(!nav.share, !ready)}>
          <i className="ti ti-download" style={{ fontSize: 18 }} aria-hidden />{t('fl_download')}
        </button>
        {!!nav.share && (
          <button onClick={() => { void share(); }} disabled={!blob} style={bigBtn(true, !blob)}>
            <i className="ti ti-share" style={{ fontSize: 18 }} aria-hidden />{t('files_share')}
          </button>
        )}
      </div>
    </div>
  );
}

/** How to install, for a phone whose browser offers no dialog of its own — Files' sheet (FilesApp: InstallHow) with
 *  the Email app's own words. Both phones are always shown, rather than guessed at. */
function InstallHow({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const step: React.CSSProperties = { padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', fontSize: 12.5, color: 'var(--text-primary)' };
  const head: React.CSSProperties = { marginBottom: 3, fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)' };
  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true" aria-label={t('emailapp_install_how_title')}
      style={{ position: 'fixed', inset: 0, zIndex: 1600, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
      <div style={{ width: 420, maxWidth: '100%', background: 'var(--bg-surface)', borderRadius: 14, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 14px', borderBottom: '1px solid var(--border-strong)' }}>
          <i className="ti ti-download" style={{ fontSize: 19, color: 'var(--accent)' }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{t('emailapp_install_how_title')}</div>
          <button onClick={onClose} title={t('chat_install_close')} aria-label={t('chat_install_close')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14 }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('emailapp_install_how_intro')}</div>
          <div style={step}><div style={head}>{t('chat_install_ios_head')}</div>{t('emailapp_install_ios')}</div>
          <div style={step}><div style={head}>{t('chat_install_android_head')}</div>{t('chat_install_android')}</div>
        </div>
      </div>
    </div>
  );
}

// What is being written: a new email, a draft opened again, or a reply / forward of one message.
type Writing = { kind: 'new' } | { kind: 'draft'; draft: Email } | { kind: 'reply'; email: Email; mode: ReplyMode };

export default function EmailApp() {
  const { t } = useTranslation();
  useEmailHead();
  useKeyboardRoom();
  // "Install app", gone once Email IS the installed app; and the sheet that says what to tap, opened straight away
  // when Chat's Email button sent this phone here for it (installApp: wantInstallHow('email')).
  const offer = useInstallOffer();
  const [howTo, setHowTo] = useState(false);
  useEffect(() => { if (takeInstallHow('email')) setHowTo(true); }, []);
  const update = useAppUpdate();

  const { accounts, activeAccountId, activeFolder, emails, emailsFor, totalEmails, isLoading, isLoadingMore, searchQuery, folders, labelOverrides, emailAppUnread,
    getActiveEmail, loadEmails, loadMoreEmails, setActiveAccount, setActiveEmail, setActiveFolder, setEmailAppUnread, setSearchQuery } = useStore();
  const open = getActiveEmail();
  const acct = accounts.find(a => a.id === activeAccountId);
  const [drawer, setDrawer] = useState(false);
  const [writing, setWriting] = useState<Writing | null>(null);
  const [sheet, setSheet] = useState<{ emailId: string; att: Attachment } | null>(null);
  const [bodyBusy, setBodyBusy] = useState(false);   // a single email's body is on its way
  const opening = useRef(0);                         // the latest of those; an older one finishing says nothing
  const [ask, setAsk] = useState<Asking | null>(null);   // the list's own question: a swipe's Delete, in Trash
  const [fellBack, setFellBack] = useState(false);       // Unread was chosen and there is none: the folder shows for now
  const seenIn = useRef('');                             // the mailbox and folder `seen` is about
  const seen = useRef<Set<string> | null>(null);         // the rows that list has shown; null until its first answer
  const askedEmpty = useRef(false);                      // Unread pressed with nothing unread: its empty message stays
  // False until the first list has come: on a phone's network that is seconds, and until then the list spins rather than
  // say there is nothing in the Inbox.
  const [firstDone, setFirstDone] = useState(false);

  // The first load, as the desktop page's: the saved layout first, so the mail opens on the saved default mailbox and
  // conversation view, the local mail at once, and new mail fetched behind it and merged silently.
  useEffect(() => {
    const s = useStore.getState();
    s.loadEmailPrefs().then(() => s.loadAccounts()).then(() => s.loadEmails()).then(() => {
      setFirstDone(true);
      const { activeAccountId: id, syncAccount } = useStore.getState();
      if (id) syncAccount(id, { auto: true }).catch(() => {});
    });
  }, []);

  // The same silent refresh as the desktop's, every 60s and skipped while composing — and at once when a phone brings the
  // app back to the front, because its timers do not run while it is away.
  useEffect(() => {
    const refresh = () => { useStore.getState().backgroundRefresh().catch(() => {}); };
    const timer = window.setInterval(refresh, 60_000);
    const onShow = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', onShow); };
  }, []);

  // A row pressed. A draft goes back into the composer, its FULL body first: the list has only a preview, and auto-save
  // would write that over the draft. So a draft that will not load does not open at all.
  const openRow = useCallback((row: Email) => {
    const s = useStore.getState();
    if (s.activeFolder === 'drafts') {
      s.loadEmail(row.id).then(full => {
        if (full) setWriting({ kind: 'draft', draft: full });
        else s.showToast(t('emailapp_load_fail'));
      });
      return;
    }
    const mine = ++opening.current;
    setBodyBusy(true);
    Promise.resolve(s.setActiveEmail(row.id)).finally(() => { if (opening.current === mine) setBodyBusy(false); });
  }, [t]);
  const retryBody = (id: string) => {
    const mine = ++opening.current;
    setBodyBusy(true);
    useStore.getState().loadEmail(id).finally(() => { if (opening.current === mine) setBodyBusy(false); });
  };
  // A conversation's star goes on all of it, as on the desktop list.
  const starRow = useCallback((row: Email) => {
    const s = useStore.getState();
    if ((row.threadCount || 1) > 1 && row.threadIds?.length) row.threadIds.forEach(id => { void s.setStar(id, !row.starred); });
    else void s.toggleStar(row.id);
  }, []);
  // A swipe right. Outside Trash the email can still be brought back from Trash, so it goes with no question, a
  // conversation whole. In Trash it is gone for good, so the Reader's question stands; Trash lists every email as a row of
  // its own (the server's flat view), so no conversation reaches it.
  const deleteRow = useCallback((row: Email) => {
    const s = useStore.getState();
    if (row.folder === 'trash') {
      setAsk({ text: t('emailapp_ask_delete'), yes: t('delete'), danger: true, run: () => { void s.deleteEmail(row.id); s.showToast(t('emailapp_deleted')); } });
    } else if ((row.threadCount || 1) > 1 && row.threadIds?.length) {
      void s.deleteEmails(row.threadIds);
      s.showToast(t('emailapp_conv_trashed'));
    } else {
      void s.deleteEmail(row.id);
      s.showToast(t('emailapp_trashed'));
    }
  }, [t]);
  // A swipe left on an unread row. The row's own message first, as in the Reader: store.markRead on another member
  // first can clear the row's unread flag, and then it takes the row's own message for read already and never saves it.
  const markRowRead = useCallback((row: Email) => {
    const s = useStore.getState();
    [row.id].concat((row.threadIds || []).filter(id => id !== row.id)).forEach(id => { void s.markRead(id); });
  }, []);
  // A swipe left on a read row. Only the row's own message, a conversation's newest, goes back to unread, as with the
  // desktop list row's button: that brings a conversation back bold as one row.
  const markRowUnread = useCallback((row: Email) => { void useStore.getState().markUnread(row.id); }, []);
  const closeWriting = () => { setWriting(null); void loadEmails({ silent: true }); };

  // Anything typed in the search box goes with the folder it was asked in, as in Gmail. The Inbox / Unread choice stays
  // as it is: only the heading row's buttons change it.
  const pickMailbox = (id: string) => { setDrawer(false); if (searchQuery) setSearchQuery(''); setActiveAccount(id); };
  const pickFolder = (f: Folder) => { setDrawer(false); if (searchQuery) setSearchQuery(''); setActiveFolder(f); };

  useBackLayers((drawer ? 1 : 0) + (open ? 1 : 0) + (writing ? 1 : 0) + (sheet ? 1 : 0) + (ask ? 1 : 0), () => {
    if (ask) setAsk(null);
    else if (sheet) setSheet(null);
    else if (writing) closeWriting();
    else if (open) setActiveEmail(null);
    else setDrawer(false);
  });

  const namedFolder = FOLDERS.find(f => f.key === activeFolder);
  const label = (folders[activeAccountId || ''] || []).find(f => f.path === activeFolder);
  const folderName = namedFolder ? t(namedFolder.labelKey) : label ? labelView(label, labelOverrides[activeAccountId || '']).name : String(activeFolder);
  const acctColor = (ACCOUNT_COLORS[acct?.color || ''] || ACCOUNT_COLORS.teal).accent;
  // Opening #2: "Allow users to sort emails by unread only or inbox (all read and unread)." The list already holds every
  // unread row of the view, not only the page's (store.loadEmails: the server's unread pass, up to its 500 newest), so
  // this only narrows what is shown, with no fetch of its own and no Load more to offer. Drafts and Snoozed get no such
  // pass, so no choice. The store keeps the whole list: an email read from here leaves this list and stays open in the
  // Reader (getActiveEmail, with its pin once a refresh has dropped the row).
  const canUnread = activeFolder !== 'drafts' && activeFolder !== 'snoozed';
  const onlyUnread = emailAppUnread && !fellBack && canUnread;
  const rows = onlyUnread ? emails.filter(e => e.unread) : emails;
  // Opening #3: "Allow users to choose by default whether it's inbox or unread. Do not change it. When they come back into
  // the email app, leave it as unread if they've chosen unread or inbox if they've chosen inbox. Let them change it
  // themselves." Then: "Unless there are no unread emails in that inbox, then automatically default back to inbox. However
  // if a new email comes in, default back to unread for that user."
  // So the choice (store.emailAppUnread, saved on the server) moves only for a press of the two buttons, and `fellBack` is
  // the automatic part, never saved, lasting until the next press or the next mailbox or folder. Someone who chose Inbox is
  // never moved: for them it decides nothing. The rows only count once they answer what the screen is asking (`answered`),
  // as the search box and the folder change before the rows do.
  // · Nothing unread, with no search running: the folder shows instead. Unread pressed with nothing unread stays on its empty
  //   message (askedEmpty) until there has been something unread to run out of.
  // · A row with an id this list has not shown arrives unread: back to Unread. That is what tells new mail from a row marked
  //   unread by hand, whose id was already here and which moves nothing. A search's read rows count as shown, so one found
  //   and marked unread there is not new mail when the search ends. Its unread rows do not, so new mail that matches still is.
  // Before the paint (useLayoutEffect), or reading the last unread would flash the empty message first.
  const listView = emailsKey(activeAccountId, activeFolder, '');
  const answered = firstDone && !isLoading && emailsFor === emailsKey(activeAccountId, activeFolder, searchQuery);
  useLayoutEffect(() => {
    if (seenIn.current !== listView) {
      seenIn.current = listView;
      seen.current = null;
      askedEmpty.current = false;
      setFellBack(false);
    }
    if (!answered) return;
    const unread = emails.filter(e => e.unread);
    const shown = seen.current;
    const arrived = !!shown && unread.some(e => !shown.has(e.id));
    const ids = shown || new Set<string>();
    emails.forEach(e => { if (!searchQuery || !e.unread) ids.add(e.id); });
    seen.current = ids;
    if (unread.length) askedEmpty.current = false;
    if (searchQuery) return;
    if (arrived) setFellBack(false);
    else if (!unread.length && !askedEmpty.current) setFellBack(true);
  }, [listView, answered, emails, searchQuery]);
  const chooseView = (only: boolean) => {
    setEmailAppUnread(only);
    setFellBack(false);
    askedEmpty.current = only && !emails.some(e => e.unread);
  };
  const viewBtn = (only: boolean, word: string) => {
    const on = onlyUnread === only;
    return (
      <button onClick={() => chooseView(only)} aria-pressed={on}
        style={{ ...oneLine, minWidth: 0, flexShrink: only ? 0 : 1, height: 32, padding: '0 14px', border: `1px solid ${on ? GMAIL.picked : 'var(--border-strong)'}`, borderRadius: 16, background: on ? GMAIL.picked : 'transparent', color: on ? GMAIL.pickedText : 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
        {word}
      </button>
    );
  };

  return (
    // The whole screen to its edges (viewport-fit=cover), so the page keeps itself clear of a notch and a home bar. The open
    // mailbox's colour replaces the app-wide teal for what is inside, the composers included, as on the desktop page.
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', padding: 'env(safe-area-inset-top) env(safe-area-inset-right) 0 env(safe-area-inset-left)', ...accentVars(acct?.color) }}>
      <style>{COMPOSE_CSS}</style>
      {update && <AppUpdateBar />}

      <div style={{ flexShrink: 0, padding: '8px 12px 0' }}>
        <div style={{ width: '100%', maxWidth: COLUMN, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 48, padding: '0 8px 0 2px', borderRadius: 24, background: GMAIL.bar }}>
            <button onClick={() => setDrawer(true)} title={t('emailapp_menu')} aria-label={t('emailapp_menu')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, padding: 0, border: 'none', borderRadius: '50%', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', flexShrink: 0 }}>
              <i className="ti ti-menu-2" style={{ fontSize: 22 }} aria-hidden />
            </button>
            {/* 16px type: an iPhone zooms the page in on any box typed into at less. The list follows once the typing
                stops (store.setSearchQuery); Enter and the × ask at once. */}
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} type="text" enterKeyHint="search" placeholder={t('searchPlaceholder')} aria-label={t('searchPlaceholder')}
              onKeyDown={e => { if (e.key === 'Enter') { e.currentTarget.blur(); void loadEmails(); } }}
              style={{ flex: 1, minWidth: 0, padding: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 16, color: 'var(--text-primary)' }} />
            {!!searchQuery && (
              <button onClick={() => { setSearchQuery(''); void loadEmails(); }} title={t('fl_preview_close')} aria-label={t('fl_preview_close')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, padding: 0, border: 'none', borderRadius: '50%', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
                <i className="ti ti-x" style={{ fontSize: 18 }} aria-hidden />
              </button>
            )}
            <button onClick={() => setDrawer(true)} title={acct ? acct.email : t('accounts')} aria-label={t('accounts')}
              style={{ width: 32, height: 32, marginLeft: 4, padding: 0, border: 'none', borderRadius: '50%', background: acctColor, color: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer', flexShrink: 0 }}>
              {acct ? initialOf(acct.name || acct.email) : <i className="ti ti-user" style={{ fontSize: 16 }} aria-hidden />}
            </button>
          </div>
          {offer.show && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button onClick={() => { offer.install().then(opened => { if (!opened) setHowTo(true); }); }}
                title={t('emailapp_install_tip')} aria-label={t('chat_install')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 9px', borderRadius: 'var(--radius-md)', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, color: EMAIL_CAL_GROUP.color, background: EMAIL_CAL_GROUP.tint, border: `1px solid ${EMAIL_CAL_GROUP.color}` }}>
                <i className="ti ti-download" style={{ fontSize: 14 }} aria-hidden />{t('chat_install')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Scheduled is its own list on the desktop too: not an emails query (store.loadEmails), and its rows carry Cancel. */}
      {activeFolder === 'scheduled' ? (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', marginTop: 8 }}><ScheduledList /></div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
          {/* Room under the last row for the Compose pill, so the last row and Load more scroll up clear of it. */}
          <div style={{ width: '100%', maxWidth: COLUMN, margin: '0 auto', paddingBottom: 'calc(88px + env(safe-area-inset-bottom))' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: canUnread ? '8px 16px 4px' : '12px 16px 4px' }}>
              {(!!searchQuery || !canUnread) && <div style={{ ...oneLine, flex: 1, minWidth: 0, fontSize: 12, color: 'var(--text-secondary)' }}>{searchQuery ? t('fl_search_results') : folderName}</div>}
              {canUnread && viewBtn(false, folderName)}
              {canUnread && viewBtn(true, t('eml_unread'))}
            </div>
            {isLoading || !firstDone ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 24, display: 'inline-block', animation: 'spin 1s linear infinite' }} aria-hidden /></div>
            ) : rows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '56px 20px', color: 'var(--text-secondary)', fontSize: 14 }}>
                <i className="ti ti-mail-opened" style={{ fontSize: 40, display: 'block', marginBottom: 10, color: 'var(--text-tertiary)' }} aria-hidden />
                {searchQuery ? t(onlyUnread ? 'emailapp_no_unread_match' : 'emailapp_no_match') : t(onlyUnread ? 'emailapp_no_unread' : 'emailapp_empty', { folder: folderName })}
              </div>
            ) : rows.map(e => (
              <SwipeRow key={e.id} email={e} folder={activeFolder} onDelete={deleteRow} onMarkRead={markRowRead} onMarkUnread={markRowUnread}>
                <Row email={e} folder={activeFolder} onOpen={openRow} onStar={starRow} />
              </SwipeRow>
            ))}
            {/* Load more — 50 at a time until every email in this view is shown, as on the desktop list. */}
            {!isLoading && firstDone && !onlyUnread && activeFolder !== 'drafts' && emails.length > 0 && emails.length < totalEmails && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '14px 0 6px' }}>
                <button onClick={() => { void loadMoreEmails(); }} disabled={isLoadingMore}
                  style={{ padding: '9px 20px', borderRadius: 20, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)', fontSize: 14, fontWeight: 500, cursor: isLoadingMore ? 'default' : 'pointer', opacity: isLoadingMore ? 0.6 : 1 }}>
                  {isLoadingMore ? t('em_loading_more') : t('em_load_more')}
                </button>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('em_shown_of', { shown: emails.length, total: totalEmails })}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <button onClick={() => setWriting({ kind: 'new' })} title={t('emailapp_compose')} aria-label={t('emailapp_compose')}
        style={{ position: 'fixed', right: 'calc(16px + env(safe-area-inset-right))', bottom: 'calc(16px + env(safe-area-inset-bottom))', zIndex: 100, display: 'flex', alignItems: 'center', gap: 10, height: 56, padding: '0 22px 0 18px', border: 'none', borderRadius: 28, background: GMAIL.compose, color: GMAIL.composeText, fontSize: 15, fontWeight: 500, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.3), 0 4px 8px 3px rgba(0,0,0,0.15)' }}>
        <i className="ti ti-pencil" style={{ fontSize: 22 }} aria-hidden />{t('emailapp_compose')}
      </button>

      <Drawer open={drawer} onClose={() => setDrawer(false)} onMailbox={pickMailbox} onFolder={pickFolder} />

      {open && (
        <Reader key={open.threadId || open.id} root={open} busy={bodyBusy} onBack={() => setActiveEmail(null)} onRetry={retryBody}
          onWrite={(email, mode) => setWriting({ kind: 'reply', email, mode })} onAttachment={(emailId, att) => setSheet({ emailId, att })} />
      )}
      {sheet && <AttachmentSheet emailId={sheet.emailId} att={sheet.att} onClose={() => setSheet(null)} />}

      {writing && writing.kind === 'reply' && <Composer email={writing.email} mode={writing.mode} onClose={() => setWriting(null)} />}
      {writing && writing.kind === 'new' && <ComposeNew onClose={() => setWriting(null)} />}
      {writing && writing.kind === 'draft' && <ComposeNew draft={writing.draft} onClose={closeWriting} />}

      {ask && <Ask text={ask.text} yes={ask.yes} danger={ask.danger} onNo={() => setAsk(null)} onYes={() => { const run = ask.run; setAsk(null); run(); }} />}
      {howTo && <InstallHow onClose={() => setHowTo(false)} />}
    </div>
  );
}
